import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Btn, Inp, Card } from "./primitives";

type Provider = {
  id: string;
  name: string;
  provider_type: string;
  tenant_id_external: string | null;
  client_id: string | null;
  allowed_email_domains: string[];
  default_role: string;
  jit_role_mapping: Record<string, string>;
  auto_provision_enabled: boolean;
  is_active: boolean;
  has_secret: boolean;
  created_at: string;
  updated_at: string;
};

const ROLES = ['admin', 'lawyer', 'legalops', 'commercial_ops', 'contract_manager', 'business_reviewer', 'requester', 'executive'];
const PROVIDER_TYPES = ['microsoft', 'google', 'okta', 'saml'];

function emptyForm(): any {
  return {
    name: '',
    provider_type: 'microsoft',
    tenant_id_external: '',
    client_id: '',
    client_secret: '',
    allowed_email_domains: '',
    default_role: 'requester',
    jit_role_mapping: '{}',
    auto_provision_enabled: true,
    is_active: false,
  };
}

function toForm(p: Provider): any {
  return {
    name: p.name,
    provider_type: p.provider_type,
    tenant_id_external: p.tenant_id_external || '',
    client_id: p.client_id || '',
    client_secret: '',
    allowed_email_domains: (p.allowed_email_domains || []).join(', '),
    default_role: p.default_role,
    jit_role_mapping: JSON.stringify(p.jit_role_mapping || {}, null, 2),
    auto_provision_enabled: p.auto_provision_enabled,
    is_active: p.is_active,
  };
}

export const SsoProvidersView = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState<Provider | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState<any>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ status: string; reason?: string; providerId?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiCall('/sso-providers');
    if (data?.providers) setProviders(data.providers);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // If this page was opened as the SSO test popup, notify opener and close.
  // Otherwise, listen for postMessage from a test popup.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('sso_test_result');
    if (result) {
      const reason = params.get('reason') || undefined;
      const providerId = params.get('provider_id') || undefined;
      if (window.opener && window.opener !== window) {
        try { window.opener.postMessage({ type: 'sso_test_result', status: result, reason, providerId }, window.location.origin); } catch {}
        window.close();
        return;
      }
      setTestResult({ status: result, reason, providerId });
      const url = new URL(window.location.href);
      url.searchParams.delete('sso_test_result');
      url.searchParams.delete('reason');
      url.searchParams.delete('provider_id');
      window.history.replaceState({}, '', url.toString());
    }

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'sso_test_result') return;
      setTestResult({ status: e.data.status, reason: e.data.reason, providerId: e.data.providerId });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const openEdit = (p: Provider) => { setEditing(p); setIsNew(false); setForm(toForm(p)); setErr(""); };
  const openAdd = () => { setEditing({} as Provider); setIsNew(true); setForm(emptyForm()); setErr(""); };
  const closeModal = () => { setEditing(null); setIsNew(false); setForm(emptyForm()); setErr(""); };

  const handleSave = async () => {
    setErr(""); setMsg("");
    // Validate JSON mapping
    let mapping: any = {};
    try {
      mapping = form.jit_role_mapping ? JSON.parse(form.jit_role_mapping) : {};
      if (typeof mapping !== 'object' || Array.isArray(mapping)) throw new Error('must be a JSON object');
    } catch (e: any) {
      setErr(`jit_role_mapping is not valid JSON: ${e.message}`);
      return;
    }
    const domains = String(form.allowed_email_domains || '').split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean);
    const body: any = {
      name: form.name,
      provider_type: form.provider_type,
      tenant_id_external: form.tenant_id_external || null,
      client_id: form.client_id || null,
      allowed_email_domains: domains,
      default_role: form.default_role,
      jit_role_mapping: mapping,
      auto_provision_enabled: !!form.auto_provision_enabled,
      is_active: !!form.is_active,
    };
    if (form.client_secret) body.client_secret = form.client_secret;

    setSaving(true);
    const result = isNew
      ? await apiCall('/sso-providers', { method: 'POST', body: JSON.stringify(body) })
      : await apiCall(`/sso-providers/${editing!.id}`, { method: 'PUT', body: JSON.stringify(body) });
    setSaving(false);
    if (result?.provider) {
      setMsg(isNew ? 'Provider created' : 'Provider updated');
      closeModal();
      load();
    } else {
      setErr(result?.error || 'Save failed');
    }
  };

  const handleDelete = async (p: Provider) => {
    if (!window.confirm(`Deactivate "${p.name}"? Users can no longer SSO via this provider. (Soft delete — flips is_active to false.)`)) return;
    const result = await apiCall(`/sso-providers/${p.id}`, { method: 'DELETE' });
    if (result?.provider) { setMsg('Provider deactivated'); load(); }
    else setErr(result?.error || 'Delete failed');
  };

  const handleTest = async (p: Provider) => {
    const result = await apiCall(`/sso-providers/${p.id}/test`, { method: 'POST', body: JSON.stringify({}) });
    if (!result?.test_url) { setErr(result?.error || 'Failed to initiate test'); return; }
    setTestResult(null);
    const w = window.open(result.test_url, 'sso_test', 'width=600,height=700,menubar=no,toolbar=no,location=no,status=no');
    if (!w) setErr('Popup blocked — enable popups for this site and try again');
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.tx }}>SSO Providers</div>
          <div style={{ fontSize: 11, color: T.txD, marginTop: 2 }}>Per-tenant OIDC configuration. Secrets are encrypted at rest (AES-256-GCM).</div>
        </div>
        <Btn v="primary" sz="sm" onClick={() => openAdd()}>+ Add Provider</Btn>
      </div>

      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.grn }}>{msg}</div>}
      {err && <div style={{ padding: "8px 12px", backgroundColor: T.redS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.red }}>{err}</div>}
      {testResult && (
        <div style={{ padding: "8px 12px", backgroundColor: testResult.status === 'success' ? T.grnS : T.redS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: testResult.status === 'success' ? T.grn : T.red }}>
          SSO test: <strong>{testResult.status}</strong>{testResult.reason ? ` — ${testResult.reason}` : ''}
          <button onClick={() => setTestResult(null)} style={{ marginLeft: 10, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>dismiss</button>
        </div>
      )}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ backgroundColor: T.bg3, borderBottom: `1px solid ${T.brd}` }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Domains</th>
              <th style={thStyle}>Default Role</th>
              <th style={thStyle}>Active</th>
              <th style={thStyle}>Has Secret</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: T.txD }}>Loading…</td></tr>}
            {!loading && providers.length === 0 && <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: T.txD }}>No SSO providers configured.</td></tr>}
            {!loading && providers.map(p => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                <td style={tdStyle}><div style={{ fontWeight: 600, color: T.tx }}>{p.name}</div></td>
                <td style={tdStyle}>{p.provider_type}</td>
                <td style={tdStyle}>{(p.allowed_email_domains || []).join(', ') || <span style={{ color: T.txD }}>—</span>}</td>
                <td style={tdStyle}>{p.default_role}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, backgroundColor: p.is_active ? T.grnS : T.bg3, color: p.is_active ? T.grn : T.txD }}>
                    {p.is_active ? 'active' : 'inactive'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <span style={{ color: p.has_secret ? T.grn : T.txD }}>{p.has_secret ? '✓' : '—'}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <Btn v="secondary" sz="sm" onClick={() => openEdit(p)}>Edit</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => handleTest(p)}>Test SSO</Btn>
                    <Btn v="secondary" sz="sm" onClick={() => handleDelete(p)}>Delete</Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: T.bg2, borderRadius: 10, maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 24, border: `1px solid ${T.brd}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 16 }}>{isNew ? 'Add SSO Provider' : `Edit: ${editing.name}`}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Inp label="Name" value={form.name} onChange={(v: string) => setForm((f: any) => ({ ...f, name: v }))} placeholder="e.g. Acme Microsoft Entra" />
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>Provider Type</div>
                <select value={form.provider_type} onChange={e => setForm((f: any) => ({ ...f, provider_type: e.target.value }))} style={selectStyle}>
                  {PROVIDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <Inp label="Tenant ID (External)" value={form.tenant_id_external} onChange={(v: string) => setForm((f: any) => ({ ...f, tenant_id_external: v }))} placeholder="Directory/Tenant GUID" />
              <Inp label="Client ID" value={form.client_id} onChange={(v: string) => setForm((f: any) => ({ ...f, client_id: v }))} placeholder="Application (client) ID" />
              <div style={{ gridColumn: '1 / -1' }}>
                <Inp
                  label={`Client Secret${!isNew && editing.has_secret ? ' (leave blank to keep existing)' : ''}`}
                  value={form.client_secret}
                  onChange={(v: string) => setForm((f: any) => ({ ...f, client_secret: v }))}
                  type="password"
                  placeholder={!isNew && editing.has_secret ? '••••••••' : 'Client secret value'}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Inp label="Allowed Email Domains (comma-separated)" value={form.allowed_email_domains} onChange={(v: string) => setForm((f: any) => ({ ...f, allowed_email_domains: v }))} placeholder="yourcompany.com" />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>Default Role</div>
                <select value={form.default_role} onChange={e => setForm((f: any) => ({ ...f, default_role: e.target.value }))} style={selectStyle}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tx, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.auto_provision_enabled} onChange={e => setForm((f: any) => ({ ...f, auto_provision_enabled: e.target.checked }))} />
                  Auto-provision
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tx, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.is_active} onChange={e => setForm((f: any) => ({ ...f, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4 }}>JIT Role Mapping (JSON)</div>
                <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>
                  Keys: exact email (wins first) or <code>domain:example.com</code>. Values: role. Falls back to Default Role.
                </div>
                <textarea
                  value={form.jit_role_mapping}
                  onChange={e => setForm((f: any) => ({ ...f, jit_role_mapping: e.target.value }))}
                  rows={5}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 8, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg3, color: T.tx }}
                  placeholder='{"admin@yourcompany.com": "admin", "domain:contractor.example.com": "requester"}'
                />
              </div>
            </div>

            {err && <div style={{ padding: "8px 12px", backgroundColor: T.redS, borderRadius: 5, marginTop: 12, fontSize: 12, color: T.red }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <Btn v="secondary" onClick={closeModal}>Cancel</Btn>
              <Btn v="primary" onClick={handleSave} disabled={saving || !form.name || !form.provider_type}>{saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle: any = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: T.txM, textTransform: 'uppercase', letterSpacing: '.04em' };
const tdStyle: any = { padding: '10px 14px', color: T.tx };
const selectStyle: any = { width: '100%', padding: '8px 10px', fontSize: 12, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg3, color: T.tx, fontFamily: 'inherit' };
